import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";
import { MORE_TESTIMONIALS } from "@/data/testimonials";
import { INTERNATIONAL_TESTIMONIALS } from "@/data/internationalTestimonials";
import { SEO_TESTIMONIALS } from "@/data/seoTestimonials";
import { optimizeUnsplashUrl } from "@/utils/imageOptimize";

const REVIEWS = [
  { name: "Camille & Julien", rating: 5, image: "https://images.unsplash.com/photo-1764380749932-57ad1cb79a07",
    fr: "Nous avons offert cette chanson à ma mère pour ses 60 ans. Elle a pleuré de joie, c'était juste parfait.",
    en: "We gifted this song to my mother for her 60th birthday. She cried tears of joy, it was just perfect." },
  { name: "Sarah & Thomas", rating: 5, image: "https://images.unsplash.com/photo-1581952975975-08cd95a728d4",
    fr: "La demande en mariage la plus originale possible. Notre chanson personnalisée a fait toute la différence.",
    en: "The most original proposal ever. Our personalized song made all the difference." },
  { name: "Léa & Marc", rating: 5, image: "https://images.unsplash.com/photo-1543342384-1f1350e27861",
    fr: "Pour l'arrivée de notre fille, une berceuse rien qu'à elle. Un souvenir que nous garderons toute la vie.",
    en: "For our daughter's arrival, a lullaby just for her. A memory we'll keep forever." },
  { name: "Antoine R.", rating: 5, image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d",
    fr: "Le format court pour Instagram est génial, j'ai partagé la chanson de mon anniversaire de mariage à tous mes amis.",
    en: "The short format for Instagram is amazing, I shared my wedding anniversary song with all my friends." },
  { name: "Nadia B.", rating: 5, image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330",
    fr: "Le certificat cadeau avec le QR code était magnifique, glissé dans une carte pour l'anniversaire de mon père.",
    en: "The gift certificate with the QR code was beautiful, slipped into a card for my father's birthday." },
  { name: "Yassine K.", rating: 4, image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e",
    fr: "Très bonne surprise, la voix est incroyablement réaliste. Petit temps d'attente mais ça valait le coup.",
    en: "Very good surprise, the voice is incredibly realistic. A bit of a wait but it was worth it." },
  { name: "Claire & Hugo", rating: 5, image: "https://images.unsplash.com/photo-1520854221256-17451cc331bf",
    fr: "Nous avons traduit notre chanson de mariage en anglais pour la famille de mon mari. Un vrai plus !",
    en: "We translated our wedding song into English for my husband's family. A real bonus!" },
  { name: "Fatou D.", rating: 5, image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80",
    fr: "J'ai créé une chanson pour la retraite de ma collègue, tout le bureau était sous le charme.",
    en: "I created a song for my colleague's retirement, the whole office was charmed." },
  { name: "Paul & Emma", rating: 5, image: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61",
    fr: "Simple, rapide, et le résultat est bluffant. Nous recommandons à 100%.",
    en: "Simple, fast, and the result is stunning. We recommend it 100%." },
  ...MORE_TESTIMONIALS,
  ...INTERNATIONAL_TESTIMONIALS,
  ...SEO_TESTIMONIALS,
];

export default function ReviewsPage() {
  const { t, language } = useI18n();
  const avgRating = (REVIEWS.reduce((s, r) => s + r.rating, 0) / REVIEWS.length).toFixed(1);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Kalypso Music",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avgRating,
      reviewCount: REVIEWS.length,
    },
    review: REVIEWS.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
      reviewBody: r[language] || r.fr,
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={language === "en" ? "Personalized Song Reviews – 111 Real Customer Testimonials | Kalypso Music" : "Avis Clients Chanson Personnalisée – 111 Témoignages Réels | Kalypso Music"}
        description={language === "en" ? "Read real reviews from customers who gifted a personalized AI song for weddings, birthdays, births and more." : "Découvrez les avis authentiques de nos clients qui ont offert une chanson personnalisée par IA pour un mariage, un anniversaire, une naissance et plus encore."}
        path="/avis"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-medium tracking-tight sm:text-4xl">{t("landing.testimonialsTitle")}</h1>
          <div className="mt-4 flex items-center justify-center gap-2">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className={`h-5 w-5 ${i < Math.round(avgRating) ? "fill-primary text-primary" : "text-muted-foreground/30"}`} />
              ))}
            </div>
            <span className="text-sm text-muted-foreground">
              {avgRating}/5 · {REVIEWS.length} {language === "en" ? "verified reviews" : "avis vérifiés"}
            </span>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {REVIEWS.map((item, i) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.1 }}
              className="overflow-hidden rounded-2xl border border-border bg-card"
              data-testid={`reviews-page-item-${i + 1}`}
            >
              <div className="h-40 w-full overflow-hidden">
                <img src={optimizeUnsplashUrl(item.image)} alt={item.name} className="h-full w-full object-cover" loading="lazy" width="48" height="48" />
              </div>
              <div className="p-6">
                <div className="flex">
                  {[...Array(item.rating)].map((_, s) => (
                    <Star key={s} className="h-3.5 w-3.5 fill-primary text-primary" />
                  ))}
                </div>
                <Quote className="mt-2 h-5 w-5 text-primary/50" />
                <p className="mt-3 text-sm leading-relaxed text-foreground/90">{item[language] || item.fr}</p>
                <p className="mt-4 font-heading text-sm font-medium text-muted-foreground">— {item.name}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Link to="/create">
            <Button size="lg" className="rounded-full px-8">{t("landing.heroCta")}</Button>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
